import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AnalysisResult from '../components/AnalysisResult';
import OutputOptions from '../components/OutputOptions';
import { SkeletonDetail } from '../components/ui/Skeleton';
import ConfirmModal from '../components/ui/ConfirmModal';
import { useToast } from '../components/ui/Toast';
import { SCENARIOS } from '../data/scenarios';

export default function RecordDetail() {
  const { recordId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    loadRecord();
  }, [recordId]);

  const loadRecord = async () => {
    try {
      const response = await fetch(`/api/snap/records?record_id=${recordId}`, { credentials: 'include' });
      const result = await response.json();

      if (result.success) {
        const r = result.data;
        let analysis = {};
        if (r.analysis_json) {
          try { analysis = JSON.parse(r.analysis_json); } catch (e) {}
        }
        let tags = [];
        if (r.tags_json) {
          try { tags = JSON.parse(r.tags_json); } catch (e) {}
        }
        let images = [];
        if (r.images_json) {
          try { images = typeof r.images_json === 'string' ? JSON.parse(r.images_json) : r.images_json; } catch (e) {}
        }
        let checklist = [];
        if (r.checklist_json) {
          try { checklist = typeof r.checklist_json === 'string' ? JSON.parse(r.checklist_json) : r.checklist_json; } catch (e) {}
        }
        setRecord({ ...r, analysis, tags, images, checklist });
      } else {
        setError(result.error || '기록을 찾을 수 없습니다');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`/api/snap/records?record_id=${recordId}`, { method: 'DELETE', credentials: 'include' });
      const result = await response.json();
      if (result.success) {
        toast.success('삭제되었습니다.');
        navigate('/records');
      } else {
        toast.error('삭제 실패: ' + result.error);
      }
    } catch (err) {
      toast.error('삭제 실패: ' + err.message);
    } finally {
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <div className="page-with-nav" style={{
        minHeight: '100vh',
        background: 'var(--gradient-bg)',
        padding: 'var(--space-lg)',
      }}>
        <SkeletonDetail />
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="page-with-nav" style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--gradient-bg)',
        color: 'white',
      }}>
        <div style={{
          textAlign: 'center',
          animation: 'fadeInUp 0.4s ease forwards',
        }}>
          <div style={{ fontSize: '3rem', marginBottom: 'var(--space-md)', opacity: 0.5 }}>🔍</div>
          <p style={{ marginBottom: 'var(--space-md)' }}>{error || '기록을 찾을 수 없습니다'}</p>
          <button onClick={() => navigate('/records')}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'var(--color-accent)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              fontWeight: 600,
            }}>
            목록으로
          </button>
        </div>
      </div>
    );
  }

  const tagPillStyle = (bg) => ({
    padding: '0.3rem 0.75rem',
    background: bg,
    color: 'white',
    borderRadius: 'var(--radius-full)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    display: 'inline-block',
  });

  return (
    <div className="page-with-nav" style={{
      minHeight: '100vh',
      background: 'var(--gradient-bg)',
      padding: 'var(--space-lg)',
    }}>
      <div style={{
        maxWidth: 'var(--max-width)',
        margin: '0 auto',
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--space-lg)',
        boxShadow: 'var(--shadow-lg)',
        animation: 'fadeInUp 0.4s ease forwards',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-md)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
            <button onClick={() => navigate('/records')}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: 'var(--color-text)',
              }}>
              ←
            </button>
            <h1 style={{
              margin: 0,
              fontSize: 'var(--font-size-xl)',
              color: 'var(--color-text)',
            }}>
              기록 상세
            </h1>
          </div>
          <button onClick={() => setShowDeleteModal(true)}
            style={{
              background: 'var(--color-danger)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              padding: '0.5rem 1rem',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              transition: 'opacity var(--transition-fast)',
            }}>
            🗑 삭제
          </button>
        </div>

        {/* Meta tags */}
        <div style={{
          display: 'flex',
          gap: 'var(--space-sm)',
          flexWrap: 'wrap',
          marginBottom: 'var(--space-md)',
        }}>
          {record.scenario_type && (
            <span style={tagPillStyle('var(--color-accent)')}>
              {SCENARIOS[record.scenario_type]
                ? `${SCENARIOS[record.scenario_type].icon} ${SCENARIOS[record.scenario_type].name}`
                : record.scenario_type}
            </span>
          )}
          {record.level && (
            <span style={tagPillStyle('#764ba2')}>
              {record.level}
            </span>
          )}
          {record.subject && (
            <span style={tagPillStyle('#e67e22')}>
              {record.subject}
            </span>
          )}
          {record.tags && record.tags.map((tag, i) => (
            <span key={i} style={{
              padding: '0.3rem 0.75rem',
              background: 'var(--color-surface-dim)',
              borderRadius: 'var(--radius-full)',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-secondary)',
              fontWeight: 500,
            }}>
              #{tag}
            </span>
          ))}
        </div>

        <div style={{
          fontSize: '0.8rem',
          color: 'var(--color-text-muted)',
          marginBottom: 'var(--space-md)',
        }}>
          {new Date(record.created_at).toLocaleString('ko-KR')}
          {record.review_count > 0 && ` | 복습 ${record.review_count}회`}
        </div>

        {/* Confidence badge */}
        {record.confidence && (
          <div style={{ marginBottom: 'var(--space-md)' }}>
            <ConfidencePill confidence={record.confidence} />
          </div>
        )}

        {/* Image(s) with lightbox */}
        {(record.image_url || (record.images && record.images.length > 0)) && (
          <div style={{ marginBottom: 'var(--space-lg)' }}>
            {/* Main image */}
            <div
              style={{
                textAlign: 'center',
                cursor: 'pointer',
              }}
              onClick={() => { setLightboxIndex(0); setLightboxOpen(true); }}
            >
              <img src={record.image_url} alt="촬영 이미지"
                style={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  borderRadius: 'var(--radius-lg)',
                  transition: 'transform var(--transition-fast)',
                  boxShadow: 'var(--shadow-sm)',
                }}
              />
            </div>
            {/* Multi-image thumbnail strip */}
            {record.images && record.images.length > 1 && (
              <div style={{
                display: 'flex',
                gap: '0.5rem',
                marginTop: 'var(--space-sm)',
                overflowX: 'auto',
                paddingBottom: '4px',
              }}>
                {record.images.map((url, i) => (
                  <img
                    key={i}
                    src={url}
                    alt={`이미지 ${i + 1}`}
                    onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }}
                    style={{
                      width: '64px',
                      height: '64px',
                      objectFit: 'cover',
                      borderRadius: 'var(--radius-md)',
                      cursor: 'pointer',
                      border: '2px solid transparent',
                      flexShrink: 0,
                      transition: 'border-color var(--transition-fast)',
                    }}
                    onMouseOver={(e) => e.currentTarget.style.borderColor = 'var(--color-accent)'}
                    onMouseOut={(e) => e.currentTarget.style.borderColor = 'transparent'}
                  />
                ))}
              </div>
            )}
            <p style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
              marginTop: 'var(--space-xs)',
              textAlign: 'center',
            }}>
              탭하여 크게 보기
              {record.images && record.images.length > 1 && ` (${record.images.length}장)`}
            </p>
          </div>
        )}

        {/* User question */}
        {record.user_question && (
          <div style={{
            padding: '0.75rem',
            background: '#f0f0ff',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--space-md)',
            fontSize: 'var(--font-size-sm)',
          }}>
            <strong>💬 질문:</strong> {record.user_question}
          </div>
        )}

        {/* Analysis result */}
        {record.analysis && Object.keys(record.analysis).length > 0 ? (
          <AnalysisResult analysis={record.analysis} scenario_type={record.scenario_type} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
            {record.problem_text && <Section title="문제" content={record.problem_text} />}
            {record.student_answer && <Section title="내 답" content={record.student_answer} bg="#ffe" />}
            {record.correct_answer && <Section title="정답" content={record.correct_answer} bg="#efe" />}
            {record.explanation && <Section title="해설" content={record.explanation} />}
            {record.key_concept && <Section title="핵심 개념" content={record.key_concept} bg="#eef" />}
          </div>
        )}

        {/* Output options */}
        <div style={{ marginTop: 'var(--space-lg)' }}>
          <OutputOptions record={record} />
        </div>
      </div>

      {/* Delete confirm modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        title="기록 삭제"
        message="이 기록을 정말 삭제하시겠습니까? 삭제된 기록은 복구할 수 없습니다."
        confirmText="삭제"
        danger
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
      />

      {/* Image lightbox */}
      {lightboxOpen && (() => {
        const allImages = record.images && record.images.length > 0
          ? record.images
          : record.image_url ? [record.image_url] : [];
        const currentImg = allImages[lightboxIndex] || allImages[0];
        if (!currentImg) return null;
        return (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 'var(--z-modal, 8000)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0,0,0,0.9)',
              animation: 'fadeIn 0.2s ease',
              cursor: 'pointer',
            }}
            onClick={() => setLightboxOpen(false)}
          >
            <img
              src={currentImg}
              alt="전체 화면 이미지"
              style={{
                maxWidth: '95%',
                maxHeight: '90vh',
                objectFit: 'contain',
                animation: 'scaleIn 0.3s ease forwards',
              }}
            />
            {allImages.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); setLightboxIndex(prev => (prev - 1 + allImages.length) % allImages.length); }}
                  style={{
                    position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)',
                    background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
                    fontSize: '1.5rem', width: '40px', height: '40px', borderRadius: '50%',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >←</button>
                <button
                  onClick={(e) => { e.stopPropagation(); setLightboxIndex(prev => (prev + 1) % allImages.length); }}
                  style={{
                    position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)',
                    background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
                    fontSize: '1.5rem', width: '40px', height: '40px', borderRadius: '50%',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >→</button>
                <div style={{
                  position: 'absolute', bottom: '1.5rem',
                  color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem',
                }}>
                  {lightboxIndex + 1} / {allImages.length}
                </div>
              </>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }}
              style={{
                position: 'absolute', top: '1rem', right: '1rem',
                background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
                fontSize: '1.5rem', width: '40px', height: '40px', borderRadius: '50%',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >✕</button>
          </div>
        );
      })()}
    </div>
  );
}

function ConfidencePill({ confidence }) {
  if (confidence === undefined || confidence === null) return null;
  const val = typeof confidence === 'string' ? parseFloat(confidence) : confidence;
  if (isNaN(val)) return null;
  const pct = Math.round(val * 100);
  let color = '#28a745';
  let label = '높음';
  if (pct < 50) { color = '#dc3545'; label = '낮음'; }
  else if (pct < 75) { color = '#ffc107'; label = '보통'; }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
      padding: '0.25rem 0.7rem', background: `${color}18`, color,
      borderRadius: 'var(--radius-full)', fontSize: 'var(--font-size-xs)',
      fontWeight: 600, border: `1px solid ${color}33`,
    }}>
      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: color }} />
      신뢰도 {pct}% ({label})
    </span>
  );
}

function Section({ title, content, bg = '#f9f9f9' }) {
  if (!content) return null;
  return (
    <div>
      <strong>{title}</strong>
      <div style={{
        padding: '0.75rem',
        background: bg,
        borderRadius: 'var(--radius-md)',
        marginTop: '0.35rem',
        whiteSpace: 'pre-wrap',
        fontSize: 'var(--font-size-sm)',
        lineHeight: 1.6,
      }}>
        {content}
      </div>
    </div>
  );
}
